import { getDatadogInstance } from '@cw-elements/analytics-provider';

import { BOOTSTRAP_ERROR_CONTEXT } from './constants';

/**
 * Datadog RUM installs its own `error`/`unhandledrejection` handlers, but only
 * once RUM is initialized — which happens partway through bootstrap. Failures
 * before that point (cw-elements/MFE setup, LaunchDarkly, the initial render)
 * would otherwise be lost.
 *
 * `installEarlyErrorCapture` buffers those early failures; `flushEarlyErrors`
 * replays them into RUM once it is initialized; `reportBootstrapError` reports
 * a single error caught by the bootstrap `try/catch`. If Datadog never
 * initializes, `addError` is a no-op, so these are always safe to call.
 */
let buffer: unknown[] = [];
let installed = false;

const handleWindowError = (event: ErrorEvent) => {
  buffer.push(event.error ?? event.message);
};

const handleRejection = (event: PromiseRejectionEvent) => {
  buffer.push(event.reason);
};

export const installEarlyErrorCapture = () => {
  if (installed) {
    return;
  }

  installed = true;
  globalThis.addEventListener('error', handleWindowError);
  globalThis.addEventListener('unhandledrejection', handleRejection);
};

export const flushEarlyErrors = () => {
  globalThis.removeEventListener('error', handleWindowError);
  globalThis.removeEventListener('unhandledrejection', handleRejection);
  installed = false;

  const rum = getDatadogInstance();

  buffer.forEach(error => rum?.addError(error, BOOTSTRAP_ERROR_CONTEXT));
  buffer = [];
};

export const reportBootstrapError = (error: unknown) => {
  getDatadogInstance()?.addError(error, BOOTSTRAP_ERROR_CONTEXT);
};
